import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { readsOf, toolsOf } from './items';
import { fieldLabels, fieldKeys, listWords, needWord } from './words';
import { dateOf, plural } from '../../../nucleus/format';

// THE PLUGIN INSPECTOR'S OWN READINGS (its setup sheet, its approval, its
// access sheet): what a plugin asks for, tool by tool, with schemas by NAME;
// how its setup stands, step by step; and the few words only these screens say.

// ── schema names ─────────────────────────────────────────────────────────────

// one read of a space's schemas, kept: id → name
const NAMES = new Map();
function loadNames(groupId) {
    const key = groupId || '';
    if (!NAMES.has(key)) {
        const p = Promise.resolve()
            .then(() => api.getSchemas({ include_json_schema: 'false', limit: 1000, ...(groupId ? { group_id: groupId } : {}) }))
            .then((r) => {
                const list = Array.isArray(r) ? r : r?.schemas || r?.data || [];
                return new Map(list.filter((x) => x?.id).map((x) => [String(x.id), x.schema_name || x.name || null]));
            })
            .catch(() => { NAMES.delete(key); return new Map(); });
        NAMES.set(key, p);
    }
    return NAMES.get(key);
}
/** Forget what was read (tests). */
export function resetSchemaNames() { NAMES.clear(); }

/** A schema's name by its id, from the space's schemas and what the plugin itself names
 *  (`data_lender_request.schema_names`); null until known. Ids are never said. */
export function useSchemaNames(groupId, plugin = null, { enabled = true } = {}) {
    const [names, setNames] = useState(null);
    useEffect(() => {
        if (!enabled) return undefined;
        let live = true;
        loadNames(groupId).then((m) => { if (live) setNames(m); });
        return () => { live = false; };
    }, [groupId, enabled]);
    const own = plugin?.data_lender_request?.schema_names || {};
    return (id) => {
        if (!id) return null;
        const k = String(id);
        return own[k] || names?.get(k) || null;
    };
}

// ── what it asks for ─────────────────────────────────────────────────────────

const UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;
/** A write's or read's schema, said: its name, never its id. */
export function schemaWord(entry, nameOf) {
    if (!entry) return 'nodes';
    if (entry.any) return 'Any node';
    if (entry.freeform) return 'Any schema';
    const named = entry.name || nameOf?.(entry.schemaId);
    if (named && !UUIDISH.test(named)) return named;
    if (entry.schemaId) return 'A schema it names';
    // a change or a delete that names no schema is of the nodes it is given
    return entry.verb === 'edit' || entry.verb === 'delete' ? 'the nodes it reads' : 'nodes';
}

const writesFrom = (decl) => (decl || []).filter(Boolean).map((d) => ({
    verb: d.action || 'create',
    schemaId: d.schema_id && d.schema_id !== '__freeform__' ? String(d.schema_id) : null,
    freeform: d.schema_id === '__freeform__',
    name: d.schema_name || null,
}));
const readsFrom = (list) => readsOf({ reads: list || [] });

/** Where its data goes: { here: true } (it runs on ofSelf), or { hosts, undeclared }. */
export function destinationOf(setup, plugin) {
    if (setup?.runs_on_paradigm === true) return { here: true, hosts: [] };
    let hosts = (setup?.data_destinations || []).filter((h) => typeof h === 'string' && h.trim());
    if (!hosts.length && setup?.endpoint_host) hosts = [setup.endpoint_host];
    if (!hosts.length) {
        const url = plugin?.plugin_endpoint_url || plugin?.mcp_server_url || plugin?.request_spec?.url || null;
        try { if (url) hosts = [new URL(url).host]; } catch { /* not a url */ }
    }
    return { here: false, hosts, undeclared: !hosts.length || !!setup?.destination_undeclared };
}

/** What a plugin asks for, tool by tool, and what it asks plugin-wide (said once):
 *  { tools: [{ id, name, reads, writes, effects }], every: { reads }, removeSource,
 *    requiresConsent, request, data: destinationOf } — from setup-config, else the
 *  plugin's own declaration. */
export function asksOf(item, setup) {
    const plugin = item?.plugin || {};
    const declared = (plugin.tools || []).filter((t) => t && t.tool_id);
    const tools = toolsOf(item, setup?.tools);
    const spec = setup?.reads;
    const toolReads = (id) => {
        if (spec && typeof spec === 'object' && !Array.isArray(spec)) return readsFrom(spec.tools?.[id]);
        const d = declared.find((t) => String(t.tool_id) === String(id));
        return readsFrom(d?.node_requirements);
    };
    let every = [];
    if (Array.isArray(spec)) every = readsFrom(spec);
    else if (spec && typeof spec === 'object') every = readsFrom(spec.plugin);
    else every = readsFrom(plugin.node_requirements?.length ? plugin.node_requirements : plugin.dlr_inputs);
    const single = tools.length === 1 && !tools[0].id;
    const list = tools.map((t) => {
        const decl = declared.find((d) => String(d.tool_id) === String(t.id)) || null;
        const state = (setup?.tools || []).find((s) => String(s.tool_id ?? '') === String(t.id ?? '')) || null;
        const effects = (state?.effects?.length ? state.effects : decl?.effects || (single ? plugin.effects : null) || []).filter(Boolean);
        let writes = writesFrom(decl ? decl.output_declaration : (single ? plugin.output_declaration : []));
        if (single) {
            writesFrom((setup?.produces || []).map((p) => ({ action: p.action, schema_id: p.schema_id, schema_name: p.schema_name })))
                .forEach((w) => { if (!writes.some((x) => x.verb === w.verb && x.schemaId === w.schemaId && x.name === w.name)) writes.push(w); });
        }
        return {
            id: t.id,
            name: t.name,
            description: t.description,
            reads: single ? every : toolReads(t.id),
            writes,
            effects: effects.map((e) => ({ description: e.description || e.kind || 'Something outside ofSelf', reversible: e.reversible !== false })),
            irreversible: effects.some((e) => e.reversible === false) || !!state?.irreversible,
        };
    });
    const pick = (k) => (setup && setup[k] !== undefined ? setup[k] : plugin[k]);
    let request = setup?.request || null;
    if (!request && plugin.execution_type === 'request' && plugin.request_spec?.url) {
        let path = '/';
        try { path = new URL(plugin.request_spec.url).pathname || '/'; } catch { path = '/'; }
        request = { method: String(plugin.request_spec.method || 'GET').toUpperCase(), path };
    }
    return {
        tools: list,
        every: single ? [] : every,
        removeSource: pick('remove_source') === true,
        requiresConsent: pick('requires_consent') === true,
        request,
        data: destinationOf(setup, plugin),
    };
}

// ── what changed since it was approved ──────────────────────────────────────

const PART_WORD = {
    reads: 'Reads', writes: 'Writes', effects: 'Outside ofSelf', destinations: 'Data goes to', tools: 'Tools',
    remove_source: 'Deletes what it reads', requires_consent: 'Asks every run', request: 'The request it makes', execution_type: 'Where it runs',
};
export const partWord = (part) => PART_WORD[part] || String(part || '').replace(/_/g, ' ');

/** A change list as rows: [{ key, word: 'Adds' | 'No longer', what, sub, warn }]. */
export function changeRows(approval, tools = []) {
    const toolName = (id) => (id == null || id === '' ? null : tools.find((t) => String(t.id) === String(id))?.name || null);
    const rows = [];
    (approval?.changes || []).forEach((c, i) => {
        const sub = [partWord(c.part), toolName(c.toolId ?? c.tool_id)].filter(Boolean).join(' · ');
        (c.added || []).forEach((what, j) => rows.push({ key: `a${i}-${j}`, word: 'Adds', what, sub, warn: true }));
        (c.removed || []).forEach((what, j) => rows.push({ key: `r${i}-${j}`, word: 'No longer', what, sub, warn: false }));
    });
    return rows;
}

// ── how its setup stands ─────────────────────────────────────────────────────

/** A scope's last path part, humanised: ".../auth/gmail.readonly" → "Gmail readonly". */
export function scopeWord(scope) {
    const last = String(scope || '').split(/[/#]/).filter(Boolean).pop() || '';
    const words = last.replace(/[._:-]+/g, ' ').trim();
    return words ? `${words[0].toUpperCase()}${words.slice(1)}` : '';
}

/** The keys a schedule's realm needs typed again (a key it was sealed to changed). */
export function reenterOf(setup) {
    const keys = new Set();
    (setup?.schedules || []).forEach((s) => (s.secrets_to_reenter || []).forEach((k) => keys.add(k)));
    (setup?.secrets_to_reenter || []).forEach((k) => keys.add(k));
    return [...keys];
}

/** How each of the four setup steps (Sign in · Keys · Settings · Turn on) stands, from the lifecycle's needs (never
 *  re-derived from flags): [{ id, title, status: done | missing | not_needed, sub }].
 *  `oauth` undefined: not read yet (the account is then said from its need alone). */
export function setupSteps({ lifecycle, oauth, credentials = [], groups = [], approval = null }) {
    const needs = lifecycle?.needs || [];
    const need = (k) => needs.find((n) => n.kind === k) || null;
    const conn = oauth?.connection || null;
    const acct = need('connection');
    const hasAccount = !!oauth?.requires_oauth || !!acct;
    const cred = need('credentials');
    const set = credentials.filter((f) => f.is_set).length;
    const params = need('parameters');
    const appr = need('approval');
    const provider = acct?.provider || oauth?.provider_label || null;
    // A MISSING STEP SAYS WHAT TO DO ("Sign in to Google Calendar"); a done
    // one says what was done. The same instruction the cards and tables give.
    return [
        {
            id: 'account', title: 'Sign in',
            status: !hasAccount ? 'not_needed' : acct ? 'missing' : 'done',
            sub: !hasAccount ? undefined
                : conn?.connected && !acct ? `Signed in${provider ? ` to ${provider}` : ''} as ${conn.account_label || 'you'}`
                    : needWord(acct ? { ...acct, provider } : { kind: 'connection', provider, reason: conn?.revoked ? 'revoked' : 'not_connected' }),
        },
        {
            id: 'credentials', title: 'Keys',
            status: !credentials.length && !cred ? 'not_needed' : cred ? 'missing' : 'done',
            sub: cred ? needWord(cred) : credentials.length ? `${plural(set, 'key')} saved` : undefined,
        },
        {
            id: 'settings', title: 'Settings',
            status: !groups.length && !params ? 'not_needed' : params ? 'missing' : 'done',
            sub: params ? needWord(params) : groups.length ? 'Filled in' : undefined,
        },
        {
            id: 'approve', title: 'Turn on',
            status: appr ? 'missing' : 'done',
            // its title says what to do; a changed one says what changed
            sub: appr
                ? (appr.reason === 'changed' || approval?.changed ? needWord({ ...appr, reason: 'changed' }, lifecycle?.approval) : undefined)
                : approval?.approvedAt ? `Turned on ${dateOf(approval.approvedAt)}` : undefined,
        },
    ];
}

/** The first step still missing, or null when every one is done. */
export const firstUnmet = (steps) => (steps || []).find((s) => s.status === 'missing')?.id || null;

/** A need's field keys (a credentials need names the ones to type again too). */
export const needKeys = (lifecycle, kind) => fieldKeys((lifecycle?.needs || []).find((n) => n.kind === kind)?.fields);

// ── the bar's primary ────────────────────────────────────────────────────────

/** The primary's words for a lifecycle's next act: Set up (the store's),
 *  Continue setup (any step still open), Run (Ready or Off). */
export function primaryWord(primary) {
    if (primary === 'install') return 'Set up';
    if (primary === 'run') return 'Run';
    return 'Continue setup';
}

/** Setup's progress, for its outline: "2 of 3 done" (the steps it needs). */
export function progressWord(steps) {
    const needed = (steps || []).filter((s) => s.status !== 'not_needed');
    return `${needed.filter((s) => s.status === 'done').length} of ${needed.length} done`;
}
